"use client"

import { useState, useRef, useEffect } from "react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "framer-motion"
import { Plus, Sparkles } from "lucide-react"
import { cn } from "@/lib/utils"

type SidebarState = 'expanded' | 'collapsed' | 'transition-opening' | 'transition-closing'

// Portal-based tooltip component for the new chat button
interface PortalTooltipProps {
  isVisible: boolean
  targetRef: React.RefObject<HTMLButtonElement | null>
  children: React.ReactNode
}

function PortalTooltip({ isVisible, targetRef, children }: PortalTooltipProps) {
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  useEffect(() => {
    if (isVisible && targetRef.current && mounted) {
      const rect = targetRef.current.getBoundingClientRect()
      setPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 8, // 8px margin from button
      })
    }
  }, [isVisible, targetRef, mounted])

  if (!mounted || !isVisible) return null

  return createPortal(
    <AnimatePresence mode="wait">
      <motion.div
        initial={{ opacity: 0, x: -5 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          position: 'fixed',
          top: `${position.top}px`,
          left: `${position.left}px`,
          transform: 'translateY(-50%)',
          zIndex: 9999,
        }}
        className="flex items-center gap-2 bg-foreground/95 text-background px-2.5 py-1.5 rounded-md text-xs whitespace-nowrap pointer-events-none"
      >
        {children}
      </motion.div>
    </AnimatePresence>,
    document.body
  )
}

interface NewChatButtonProps {
  isCollapsed: boolean
  sidebarState: SidebarState
  onNewChat: () => void
}

export function NewChatButton({ isCollapsed, sidebarState, onNewChat }: NewChatButtonProps) {
  const [isHovered, setIsHovered] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Determine when to show/hide text based on state
  const showText = sidebarState === 'expanded' || sidebarState === 'transition-opening'

  // Different animation delays for opening vs closing
  const textDelay = sidebarState === 'transition-opening' ? 0.2 : 0
  const textDuration = sidebarState === 'transition-closing' ? 0 : 0.15
  return (
    <div className="px-3 pb-3 shrink-0">
      <div className="relative h-10 group">
        <button
          ref={buttonRef}
          onClick={onNewChat}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={cn(
            "absolute left-0 h-10 transition-all",
            isCollapsed
              ? "w-10 rounded-lg"
              : "w-full rounded-xl bg-linear-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-md hover:shadow-lg"
          )}
        />
        <Plus className={cn(
          "absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 transition-transform duration-300 ease-out pointer-events-none z-10",
          "group-hover:rotate-90",
          isCollapsed ? "text-primary-600" : "text-white"
        )} />
        <AnimatePresence>
          {showText && (
            <>
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: textDuration } }}
                transition={{ duration: 0.15, delay: textDelay }}
                className="absolute left-0 right-0 top-1/2 -translate-y-1/2 font-medium text-center text-white pointer-events-none z-10"
              >
                Neuer Chat
              </motion.span>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: textDuration } }}
                transition={{ duration: 0.15, delay: textDelay }}
                className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none z-10"
              >
                <Sparkles className="h-3 w-3 animate-pulse text-white" />
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Tooltip for collapsed state */}
        {isCollapsed && (
          <PortalTooltip isVisible={isHovered} targetRef={buttonRef}>
            <Plus className="h-3.5 w-3.5" />
            <span>Neuer Chat</span>
          </PortalTooltip>
        )}
      </div>
    </div>
  )
}